import { ERelationType, ICmsModelData } from '@df/catalog/cms/cms-model.interface';
import { inject } from '@angular/core';
import { IModelData, IModelDataV1, Model, TLoadParams } from '@df/catalog/model';
import { Client, ISizeName } from '@df/core/service/client';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { difference } from '@df/util/fn/difference';
import { ICmsDraft } from '@icms/core/resource/draft/icms-draft';
import defaultsDeep from '@df/util/fn/defaults-deep';
import cloneDeep from 'lodash-es/cloneDeep';
import each from 'lodash-es/each';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import { ICmsDataChangeService, NICmsDataChange } from './../../data/change/icms-data-change.service';
import { ICmsDataScopeService } from './../../data/scope/icms-data-scope.service';

export class ICmsModel<D extends IModelDataV1 = IModelData, I extends string | number = number> extends Model<D, I> {
    /**
     * Data changes service
     */
    @NgInject(ICmsDataChangeService)
    private iCmsDataChangeService!: ICmsDataChangeService;

    /**
     * Client service
     */
    @NgInject(Client)
    private client!: Client;

    /**
     * Client service
     */
    @NgInject(ICmsDataScopeService)
    private iCmsDataScopeService!: ICmsDataScopeService;

    @NgInject(TimeoutService)
    private timeoutService!: TimeoutService;

    /**
     * Pers size scope data
     */
    private scopeData: { [sizeId: string]: D | undefined } = {};

    /**
     * If works with scope data
     */
    hasScopeData = true;

    /**
     * If works with drafs data
     */
    usesDrafts = false;

    /**
     * If model uses cms top bar for saving, undo and redo
     */
    usesMainSaveBar = false;

    /**
     * Original data comming from BE (free from CMS changes)
     */
    orgData?: D;

    /**
     * If data can be overwritten
     *
     * We only allowe this while saving
     */
    allowDataOverwrite = false;

    private draft?: ICmsDraft<D>;
    private draftData: D | undefined;
    /**
     * When draft is active we do not allow data overwrite
     * but we keep most rectent information here
     * Once draft is released we aplly it
     */
    private dataShadowedByDraft?: D;

    constructor(id: I) {
        super(id);

        // when size changes we need to notify that data has changed
        this.timeoutService.setTimeout(() => {
            if (this.hasScopeData) {
                let lastSizeName = this.client.size;
                this.client.obs.breakpoint.subscribe(() => {
                    if (this.client.size !== lastSizeName) {
                        const previousValue = this.scopeData[lastSizeName];
                        if (previousValue) {
                            const event: NICmsDataChange.IEventChange = {
                                changeType: NICmsDataChange.EChangeType.change,
                                model: this as ICmsModel,
                                value: this.data,
                                previousValue,
                                valuePath: ['data'],
                                changedObject: this,
                                flag: 'dataSwapOnSize'
                            };
                            this.iCmsDataChangeService.notify(event, () => {
                                lastSizeName = this.client.size;
                            });
                        } else {
                            lastSizeName = this.client.size;
                        }
                    }
                });
            }
        });
    }

    /**
     * Makes sure cms models are loaded with max verbosity when viewed
     */
    override async load(verbosity: number = 1, params?: TLoadParams): Promise<void> {
        if (this.client.isCms && verbosity === 3) {
            verbosity = this.maxVerbosity;
        }
        return super.load(verbosity, params);
    }

    setDraft(draft: ICmsDraft<D> | undefined) {
        if (!this.usesDrafts) {
            throw new Error(`Setting draft for non supporting model: ${this.type} ${this.id}`);
        }
        if (this.draft === draft && (!draft || this.draftData === draft.data!.draftData)) {
            // do nothing unless draf or its data model changed
            return;
        }
        // in case we have fresh draft based on model data changes dont notify anything
        if (draft && draft.data!.draftData === this.rawData) {
            this.draft = draft;
            this.draftData = draft ? draft.data!.draftData : undefined;
        } else {
            const sizeName: ISizeName = this.client.size;
            const data = draft ? draft.data!.draftData : this.dataShadowedByDraft || this._data;
            const scopeData = this.iCmsDataScopeService.transform(data, sizeName, this as ICmsModel, ['data']);
            const event: NICmsDataChange.IEventChange = {
                changeType: NICmsDataChange.EChangeType.change,
                model: this as ICmsModel,
                value: scopeData,
                previousValue: this.data,
                valuePath: ['data'],
                changedObject: this,
                flag: 'dataInit'
            };
            this.iCmsDataChangeService.notify(event, () => {
                this.draft = draft;
                this.draftData = draft ? draft.data!.draftData : undefined;
                if (!draft && this.dataShadowedByDraft) {
                    this._data = this.dataShadowedByDraft;
                    this.dataShadowedByDraft = undefined;
                }
                this.scopeData = {
                    [sizeName]: scopeData
                };
            });
        }
    }

    getDraft() {
        return this.draft;
    }

    override dataSetter(data: D | undefined, options?: { trueChange?: boolean; reset?: boolean }) {
        if (data) {
            this.sanitize(data);
        }
        // if draft is active dont amend model
        if (this.draft) {
            this.dataShadowedByDraft = data;
            return;
        }
        this.dataShadowedByDraft = undefined;
        // in events we operate on scope data and not its source
        const sizeName: ISizeName = this.client.size;
        const scopeData = this.hasScopeData
            ? data && this.iCmsDataScopeService.transform(data, sizeName, this as ICmsModel, ['data'])
            : data;
        // only if we are changing things
        const event: NICmsDataChange.IEventChange = {
            changeType: NICmsDataChange.EChangeType.change,
            model: this as ICmsModel,
            value: scopeData,
            previousValue: this.data,
            valuePath: ['data'],
            changedObject: this,
            flag: options?.trueChange ? undefined : options?.reset ? 'dataReset' : 'dataInit'
        };
        this.iCmsDataChangeService.notify(event, () => {
            if (this.hasScopeData) {
                this.scopeData = {
                    [sizeName]: scopeData
                };
            }
            if (!options?.trueChange) {
                this.orgData = undefined;
            }

            super.dataSetter(data);
        });
    }

    protected override set(data: D, verbosity: number): void {
        // dont override element with data changes
        if (verbosity && this.verbosity === verbosity && this.client.isCms && !this.allowDataOverwrite && this.hasDataChanges()) {
            return;
        }
        return super.set(data, verbosity);
    }
    override unload(): void {
        // only if we are changing things
        const event: NICmsDataChange.IEventDelete = {
            changeType: NICmsDataChange.EChangeType.delete,
            model: this as ICmsModel,
            previousValue: this.data,
            valuePath: ['data'],
            changedObject: this
        };
        this.iCmsDataChangeService.notify(event, () => {
            this._data = undefined;
            this.orgData = undefined;
            this.scopeData = {};
            this.verbosity = 0;
        });
    }

    protected override dataGetter(): D | undefined {
        const _data = this.rawData;
        if (!_data || !this.hasScopeData) {
            return _data;
        }
        const sizeName: ISizeName = this.client.size;
        if (!this.scopeData[sizeName]) {
            this.scopeData[sizeName] = this.iCmsDataScopeService.transform(_data, sizeName, this as ICmsModel, ['data']);
        }
        return this.scopeData[sizeName];
    }

    /**
     * Data getter
     */
    override get rawData(): D | undefined {
        return this.draftData || this._data;
    }

    swapDataForDraft() {
        const existingData = this._data;
        this._data = cloneDeep(existingData);
        return existingData;
    }

    /**
     * Just a helper method when debugging
     */
    testChanges() {
        console.log(difference(this.rawData, this.orgData, true));
    }

    /**
     * Checks if model has data changes
     */
    hasDataChanges(): boolean {
        if (!this._data) {
            return false;
        }
        if ((this.id as number) < 0) {
            return true;
        }
        if (this.draft) {
            return this.draft.hasDataChanges();
        }
        return this.rawData && isEmpty(difference(this.rawData, this.orgData || {}, true)) ? false : true;
    }

    getScopeDataEntries(): D[] {
        return Object.values(this.scopeData).filter(Boolean) as D[];
    }

    /**
     * If model is a mirror getter
     */
    get isMirror(): boolean {
        return !!(this._data as any as ICmsModelData)?._cms?.multisite?.relations?.find(
            relation => relation.child === this.id && relation.relationType === ERelationType.mirror
        );
    }

    /**
     * Reset data to org
     */
    resetData() {
        this.dataSetter(cloneDeep(this.orgData), { reset: true });
    }

    /**
     * Copies critial things from newly created model to existing one
     */
    merge(model: this) {
        // remove this model from resource
        this.resource.setAsset(this.id, undefined);
        // take place of the consumed model
        this.id = model.id;
        this.resource.setAsset(model.id, this);
        // override critical data
        this.verbosity = model.verbosity;
        this.data = model.orgData || model.rawData;
        // remove consumed model
        // delete model;
        // this.resource.unload(oldId);
        this.next();
    }

    protected sanitize(data: D) {
        //  injecting defaults in case field definitions changed
        if (data.verbosity >= this.maxVerbosity && typeof data.id === 'number' && data.id > 0) {
            const initialData = this.getInitialDataForSanitizer(data);
            if (initialData) {
                defaultsDeep(data, initialData);
            }
        }

        // in cms mode try to keep as much from data as we have
        if (this.client.isCms && this._data) {
            each(this._data, (value, key) => {
                if (isEqual(data[key], value)) {
                    data[key] = value;
                }
            });
        }
    }

    /**
     * Initial data for sanitizer
     */
    protected getInitialDataForSanitizer(data: D): Partial<D> | undefined {
        return undefined;
    }
}
